/**
 * SharePoint Service - Microsoft Graph API
 * All operations use Microsoft Graph API instead of SharePoint REST API
 */

import axios from "axios";
import { sharePointConfig } from "../config/authConfig";

class SharePointService {
  constructor() {
    this.accessToken = null;
    this.graphBaseUrl = "https://graph.microsoft.com/v1.0";
    this.siteUrl = sharePointConfig.siteUrl;
    this.listNames = sharePointConfig.listNames;
    this.siteId = null;
    this.listIds = new Map(); // Cache list IDs
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  getHeaders() {
    return {
      Authorization: `Bearer ${this.accessToken}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Get Site ID from SharePoint URL
   * Example: https://tenant.sharepoint.com/sites/sitename
   */
  async getSiteId() {
    if (this.siteId) return this.siteId;

    try {
      const url = new URL(this.siteUrl);
      const hostname = url.hostname;
      const sitePath = url.pathname;

      const endpoint = `${this.graphBaseUrl}/sites/${hostname}:${sitePath}`;

      const response = await axios.get(endpoint, {
        headers: this.getHeaders(),
      });

      this.siteId = response.data.id;
      console.log("[SharePoint] Site ID:", this.siteId);
      return this.siteId;
    } catch (error) {
      console.error("[SharePoint] Error getting site ID:", error);
      throw new Error(`Failed to get site ID: ${error.message}`);
    }
  }

  /**
   * Get List ID by list name
   */
  async getListId(listName) {
    if (this.listIds.has(listName)) {
      return this.listIds.get(listName);
    }

    try {
      const siteId = await this.getSiteId();
      const endpoint = `${this.graphBaseUrl}/sites/${siteId}/lists`;

      const response = await axios.get(endpoint, {
        headers: this.getHeaders(),
        params: {
          $filter: `displayName eq '${listName}'`,
          $select: "id,displayName",
        },
      });

      if (response.data.value && response.data.value.length > 0) {
        const listId = response.data.value[0].id;
        this.listIds.set(listName, listId);
        console.log(`[SharePoint] List '${listName}' ID:`, listId);
        return listId;
      }

      throw new Error(`List '${listName}' not found`);
    } catch (error) {
      console.error(
        `[SharePoint] Error getting list ID for ${listName}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Transform Graph API item to our format
   * Graph API stores custom fields in 'fields' object
   */
  transformItem(item) {
    return {
      Id: item.id,
      ...item.fields,
      Created: item.createdDateTime,
      Modified: item.lastModifiedDateTime,
    };
  }

  /**
   * Generic GET request to list items
   */
  async getListItems(listName, options = {}) {
    try {
      const siteId = await this.getSiteId();
      const listId = await this.getListId(listName);

      const endpoint = `${this.graphBaseUrl}/sites/${siteId}/lists/${listId}/items`;

      const params = {
        $expand: "fields",
        $top: options.top || 5000,
      };

      if (options.filter) params.$filter = options.filter;
      if (options.select) params.$select = options.select;
      if (options.orderby) params.$orderby = options.orderby;

      const response = await axios.get(endpoint, {
        headers: this.getHeaders(),
        params,
      });

      return response.data.value.map((item) => this.transformItem(item));
    } catch (error) {
      console.error(`[SharePoint] Error fetching ${listName}:`, error);
      throw error;
    }
  }

  /**
   * Create list item
   */
  async createListItem(listName, itemData) {
    try {
      const siteId = await this.getSiteId();
      const listId = await this.getListId(listName);

      const endpoint = `${this.graphBaseUrl}/sites/${siteId}/lists/${listId}/items`;

      const response = await axios.post(
        endpoint,
        {
          fields: itemData,
        },
        {
          headers: this.getHeaders(),
        },
      );

      return this.transformItem(response.data);
    } catch (error) {
      console.error(`[SharePoint] Error creating item in ${listName}:`, error);
      throw error;
    }
  }

  /**
   * Update list item
   */
  async updateListItem(listName, itemId, itemData) {
    try {
      const siteId = await this.getSiteId();
      const listId = await this.getListId(listName);

      const endpoint = `${this.graphBaseUrl}/sites/${siteId}/lists/${listId}/items/${itemId}`;

      const response = await axios.patch(
        endpoint,
        {
          fields: itemData,
        },
        {
          headers: this.getHeaders(),
        },
      );

      return this.transformItem(response.data);
    } catch (error) {
      console.error(`[SharePoint] Error updating item in ${listName}:`, error);
      throw error;
    }
  }

  /**
   * Delete list item
   */
  async deleteListItem(listName, itemId) {
    try {
      const siteId = await this.getSiteId();
      const listId = await this.getListId(listName);

      const endpoint = `${this.graphBaseUrl}/sites/${siteId}/lists/${listId}/items/${itemId}`;

      await axios.delete(endpoint, {
        headers: this.getHeaders(),
      });

      return { success: true };
    } catch (error) {
      console.error(
        `[SharePoint] Error deleting item from ${listName}:`,
        error,
      );
      throw error;
    }
  }

  // ==================== ASSETS ====================

  async getAssets() {
    return this.getListItems(this.listNames.assets);
  }

  async createAsset(assetData) {
    return this.createListItem(this.listNames.assets, assetData);
  }

  async updateAsset(assetId, assetData) {
    return this.updateListItem(this.listNames.assets, assetId, assetData);
  }

  async deleteAsset(assetId) {
    return this.deleteListItem(this.listNames.assets, assetId);
  }

  // ==================== ENTITIES ====================

  async getEntitiesForAsset(assetId) {
    // Get all entities (can't filter on non-indexed lookup fields in Graph API)
    const allEntities = await this.getListItems(this.listNames.entities);

    // Filter in JavaScript
    return allEntities.filter((entity) => {
      // Check if AssetId matches (lookup field ID)
      return (
        entity.AssetIdLookupId === assetId ||
        entity.AssetIdLookupId === parseInt(assetId) ||
        entity.AssetIdLookupId === assetId ||
        entity.AssetIdLookupId === parseInt(assetId)
      );
    });
  }

  async createEntity(entityData) {
    return this.createListItem(this.listNames.entities, entityData);
  }

  async updateEntity(entityId, entityData) {
    return this.updateListItem(this.listNames.entities, entityId, entityData);
  }

  async deleteEntity(entityId) {
    return this.deleteListItem(this.listNames.entities, entityId);
  }

  // ==================== SIGNALS ====================

  async getSignalsForAsset(assetId) {
    // Get all entities for this asset first
    const entities = await this.getEntitiesForAsset(assetId);
    const entityIds = entities.map((e) => e.Id);

    if (entityIds.length === 0) return [];

    // Get all signals (can't filter efficiently on non-indexed lookup fields)
    const allSignals = await this.getListItems(this.listNames.entitySignals);
    // Filter in JavaScript and sort
    const filteredSignals = allSignals.filter((signal) => {
      const entityId = signal.EntityIdLookupId;
      return (
        entityIds.includes(entityId) || entityIds.includes(String(entityId))
      );
    });
    // Sort by Timestamp descending
    return filteredSignals.sort(
      (a, b) => new Date(b.Timestamp) - new Date(a.Timestamp),
    );
  }

  /**
   * Check for duplicate signal and return existing signal if found
   * NOW: Returns existing signal for UPDATE instead of throwing error
   */
  async checkDuplicateSignal(entityId, signalType, timestamp) {
    const signals = await this.getListItems(this.listNames.entitySignals);

    const signalDate = new Date(timestamp).toISOString().split("T")[0]; // YYYY-MM-DD

    const existingSignal = signals.find((signal) => {
      const existingDate = new Date(signal.Timestamp)
        .toISOString()
        .split("T")[0];
      return (
        signal.EntityIdLookupId == entityId &&
        signal.SignalType == signalType &&
        existingDate == signalDate
      );
    });

    return existingSignal || null; // Return existing signal or null
  }

  /**
   * Create or update signal
   * NOW: Updates existing signal if duplicate found
   */
  async createSignal(signalData) {
    const existingSignal = await this.checkDuplicateSignal(
      signalData.EntityIdLookupId,
      signalData.SignalType,
      signalData.Timestamp,
    );

    if (existingSignal) {
      // UPDATE existing signal
      console.log(
        `[SharePoint] Updating existing signal ID ${existingSignal.Id} for entity ${signalData.EntityIdLookupId}`,
      );
      return this.updateListItem(
        this.listNames.entitySignals,
        existingSignal.Id,
        signalData,
      );
    } else {
      // CREATE new signal
      console.log(
        `[SharePoint] Creating new signal for entity ${signalData.EntityIdLookupId}`,
      );
      return this.createListItem(this.listNames.entitySignals, signalData);
    }
  }

  async updateSignal(signalId, signalData) {
    return this.updateListItem(
      this.listNames.entitySignals,
      signalId,
      signalData,
    );
  }

  async deleteSignal(signalId) {
    return this.deleteListItem(this.listNames.entitySignals, signalId);
  }

  // ==================== RULES ====================

  async getRulesForAsset(assetId) {
    // Get entities for asset
    const entities = await this.getEntitiesForAsset(assetId);
    const entityIds = entities.map((e) => e.Id);

    if (entityIds.length === 0) return [];

    // Get all rules (can't filter efficiently on non-indexed lookup fields)
    const allRules = await this.getListItems(this.listNames.dependencyRules);

    // Filter in JavaScript - get rules where source or target is one of these entities
    return allRules.filter((rule) => {
      const sourceId = rule.SourceEntityIdLookupId || rule.SourceEntityId;
      const targetId = rule.TargetEntityIdLookupId || rule.TargetEntityId;

      return (
        entityIds.includes(sourceId) ||
        entityIds.includes(String(sourceId)) ||
        entityIds.includes(targetId) ||
        entityIds.includes(String(targetId))
      );
    });
  }

  async createRule(ruleData) {
    return this.createListItem(this.listNames.dependencyRules, ruleData);
  }

  async updateRule(ruleId, ruleData) {
    return this.updateListItem(
      this.listNames.dependencyRules,
      ruleId,
      ruleData,
    );
  }

  async deleteRule(ruleId) {
    return this.deleteListItem(this.listNames.dependencyRules, ruleId);
  }

  // ==================== ALERTS ====================

  async getAlerts(assetId, unresolvedOnly = false) {
    // Get all alerts (can't filter on non-indexed lookup fields)
    const allAlerts = await this.getListItems(this.listNames.alertsLog);

    // Filter in JavaScript
    const filteredAlerts = allAlerts.filter((alert) => {
      const alertAssetId = alert.AssetIdLookupId || alert.AssetIdLookupId;
      const matchesAsset =
        alertAssetId === assetId ||
        alertAssetId === parseInt(assetId) ||
        String(alertAssetId) === String(assetId);

      if (!matchesAsset) return false;

      if (unresolvedOnly) {
        return !alert.IsResolved || alert.IsResolved === false;
      }

      return true;
    });

    // Sort by Created descending
    return filteredAlerts.sort(
      (a, b) => new Date(b.Created) - new Date(a.Created),
    );
  }

  async createAlert(alertData) {
    return this.createListItem(this.listNames.alertsLog, {
      ...alertData,
      IsResolved: false,
    });
  }

  async resolveAlert(alertId) {
    return this.updateListItem(this.listNames.alertsLog, alertId, {
      IsResolved: true,
      ResolvedOn: new Date().toISOString(),
    });
  }

  // ==================== ROLE ASSIGNMENTS ====================

  async getRoleAssignments() {
    return this.getListItems(this.listNames.roleAssignments);
  }

  async getUserRole(userEmail) {
    // Get all role assignments (UserEmail might not be indexed)
    const allAssignments = await this.getListItems(
      this.listNames.roleAssignments,
    );

    // Filter in JavaScript
    const userAssignment = allAssignments.find(
      (assignment) =>
        assignment.UserEmail &&
        assignment.UserEmail.toLowerCase() === userEmail.toLowerCase(),
    );

    if (userAssignment) {
      return userAssignment.Role;
    }

    return "Viewer"; // Default role
  }

  // Add these methods to sharePointService class:

  // ==================== ROLE ASSIGNMENTS ====================

  async getRoleAssignments() {
    return this.getListItems(this.listNames.roleAssignments);
  }

  async createRoleAssignment(roleData) {
    return this.createListItem(this.listNames.roleAssignments, roleData);
  }

  async updateRoleAssignment(assignmentId, roleData) {
    return this.updateListItem(
      this.listNames.roleAssignments,
      assignmentId,
      roleData,
    );
  }

  async deleteRoleAssignment(assignmentId) {
    return this.deleteListItem(this.listNames.roleAssignments, assignmentId);
  }

  // ==================== NOTIFICATION SETTINGS ====================

  async getNotificationSettings() {
    try {
      const items = await this.getListItems(this.listNames.notificationSettings);
      const row = items.find((i) => i.Title === "global");
      if (!row) return null;
      return {
        _id: row.Id,
        panicDays: row.PanicDays ?? 30,
        enableCSF: row.EnableCSF ?? true,
        enableDeadlines: row.EnableDeadlines ?? true,
        enableMetricMiss: row.EnableMetricMiss ?? true,
        enableRuleFailures: row.EnableRuleFailures ?? true,
        enableAutoPrompt: row.EnableAutoPrompt ?? false,
        scheduleDays: (() => {
          try { return JSON.parse(row.ScheduleDays || "[1,4]"); } catch { return [1, 4]; }
        })(),
        scheduleTime: row.ScheduleTime || "08:00",
        lastSent: {
          csf: row.LastSentCSF || null,
          metrics: row.LastSentMetrics || null,
          rules: row.LastSentRules || null,
          deadline: row.LastSentDeadline || null,
        },
      };
    } catch (error) {
      console.error("[SharePoint] Error fetching notification settings:", error);
      return null;
    }
  }

  async saveNotificationSettings(settings, lastSentUpdates = {}) {
    const fields = {
      Title: "global",
      PanicDays: settings.panicDays,
      EnableCSF: settings.enableCSF,
      EnableDeadlines: settings.enableDeadlines,
      EnableMetricMiss: settings.enableMetricMiss,
      EnableRuleFailures: settings.enableRuleFailures,
      EnableAutoPrompt: settings.enableAutoPrompt,
      ScheduleDays: JSON.stringify(settings.scheduleDays || [1, 4]),
      ScheduleTime: settings.scheduleTime || "08:00",
      ...(lastSentUpdates.csf && { LastSentCSF: lastSentUpdates.csf }),
      ...(lastSentUpdates.metrics && { LastSentMetrics: lastSentUpdates.metrics }),
      ...(lastSentUpdates.rules && { LastSentRules: lastSentUpdates.rules }),
      ...(lastSentUpdates.deadline && { LastSentDeadline: lastSentUpdates.deadline }),
    };

    if (settings._id) {
      return this.updateListItem(this.listNames.notificationSettings, settings._id, fields);
    }
    return this.createListItem(this.listNames.notificationSettings, fields);
  }

  async updateNotificationLastSent(settingsId, lastSentUpdates) {
    const fields = {};
    if (lastSentUpdates.csf) fields.LastSentCSF = lastSentUpdates.csf;
    if (lastSentUpdates.metrics) fields.LastSentMetrics = lastSentUpdates.metrics;
    if (lastSentUpdates.rules) fields.LastSentRules = lastSentUpdates.rules;
    if (lastSentUpdates.deadline) fields.LastSentDeadline = lastSentUpdates.deadline;
    if (!Object.keys(fields).length) return;
    return this.updateListItem(this.listNames.notificationSettings, settingsId, fields);
  }

  async getUserRole(userEmail) {
    try {
      // Get all role assignments
      const allAssignments = await this.getListItems(
        this.listNames.roleAssignments,
      );

      // Find user's assignment (case-insensitive)
      const userAssignment = allAssignments.find(
        (assignment) =>
          assignment.UserEmail &&
          assignment.UserEmail.toLowerCase() === userEmail.toLowerCase(),
      );

      if (userAssignment) {
        // Check if user is blocked
        if (userAssignment.Role === "Blocked") {
          return "Blocked";
        }
        return userAssignment.Role;
      }

      // Default role for authenticated users not in the list
      return "Viewer";
    } catch (error) {
      console.error("[SharePoint] Error getting user role:", error);
      return "Viewer"; // Default role on error
    }
  }
}

export default new SharePointService();
